import React, { useRef, useEffect, useMemo } from 'react';

import { isRtl } from '@utils/dom.ts';
import { getContainerRect, getContainerWidth, toContainerCoords } from '@utils/mapElement.ts';

type ArrowPosition = 'bottom' | 'left' | 'right' | 'top' | 'topright' | 'bottom-topright';

type KeyboardLikeEvent = {
  key: string;
};

type ContainerPosition = ReturnType<typeof toContainerCoords>;

let hideTimerClearRef: ReturnType<typeof setTimeout> | null = null; // holds the setTimeout return value used to clear timeouts
let globalListenersInitialized = false; // ensures one-time setup runs only once across all instances

const hideTooltip = () => {
  document.getElementById('tooltip')?.setAttribute('style', 'display: none;');
  cancelHide(); // clear any timers and dereference the timer clear ref
};

const handleKeyboardSelect = (callback: (event: KeyboardLikeEvent) => void) => (event: KeyboardLikeEvent) => {
  if (event.key === 'Enter' || event.key === ' ') {
    hideTooltip();
    callback(event);
  }
};

const cancelHide = () => {
  if (hideTimerClearRef) {
    clearTimeout(hideTimerClearRef);
  }
  hideTimerClearRef = null;
};

type TooltipProps = {
  arrowPosition?: ArrowPosition;
  avoidCover?: boolean;
  cancelTooltip?: boolean;
  children: React.ReactNode;
  className?: string;
  dataTestId?: string;
  gutter?: number;
  isVisible?: boolean;
  placement?: string;
  text?: string;
};

const Tooltip = ({
  className,
  text,
  arrowPosition,
  children,
  isVisible = true,
  dataTestId = 'tooltipLayer',
  cancelTooltip = false,
  avoidCover = false,
}: TooltipProps) => {
  const actualArrowPosition = useMemo<ArrowPosition>(() => {
    if (isRtl()) {
      if (arrowPosition === 'left') {
        return 'right';
      }
      if (arrowPosition === 'right') {
        return 'left';
      }
    }
    return arrowPosition ?? 'bottom';
  }, [arrowPosition]);
  const componentRef = useRef<HTMLDivElement>(null);

  // One-time global setup: attach the Escape key listener and wire up the shared #tooltip DOM
  // node. Runs inside useEffect so it only executes when the component actually mounts in a browser.
  useEffect(() => {
    if (globalListenersInitialized) {
      return;
    }
    globalListenersInitialized = true;

    // Allow user to clear the tooltip instantly with Escape key
    document.addEventListener('keydown', event => {
      if (event.key === 'Escape') {
        hideTooltip(); // Insta-GONE!
      }
    });

    const tooltip = document.getElementById('tooltip');
    tooltip?.addEventListener('mouseenter', cancelHide);
    tooltip?.addEventListener('mouseleave', hideTooltip);
  }, []);

  useEffect(() => {
    if (!isVisible) {
      hideTooltip();
    } // be warned, this will hide ANY tooltip, not just "our's"...
  }, [isVisible]);

  // Hide immediately whenever cancelTooltip toggles on
  useEffect(() => {
    if (cancelTooltip) {
      hideTooltip();
    }
  }, [cancelTooltip]);

  const calculateTooltipPosition = (
    position: ContainerPosition,
    actualArrowPosition: ArrowPosition,
    isRtlDirection: boolean,
  ) => {
    let topOffset = position.top; // set default position of tooltip to the top of element
    let leftOffset = position.left; // set default position of tooltip to the left of element

    if (actualArrowPosition === 'top' || actualArrowPosition === 'topright') {
      topOffset = position.bottom;
    }
    if (actualArrowPosition === 'left' || actualArrowPosition === 'right') {
      topOffset += position.height / 2;
    }
    if (actualArrowPosition === 'left') {
      leftOffset = position.right;
    }
    if (actualArrowPosition === 'right') {
      leftOffset = position.left;
    }
    if (actualArrowPosition === 'topright' || actualArrowPosition === 'bottom-topright') {
      // For topright variants the tooltip is edge-anchored, not centered.
      // In RTL the edge anchor mirrors to the element's left edge.
      leftOffset = isRtlDirection ? position.left : position.right;
    }
    if (actualArrowPosition === 'top' || actualArrowPosition === 'bottom') {
      leftOffset += position.width / 2;
    }

    // When avoidCover is true and arrow on left, push tooltip further right so it never sits atop trigger.
    if (avoidCover && actualArrowPosition === 'left') {
      leftOffset += 14;
    } // gap between trigger and tooltip (accounts for arrow width ~9px)

    return { topOffset, leftOffset };
  };

  /* Return flipped position if element overflows the container  */
  const checkArrowPosition = (
    actualArrowPosition: ArrowPosition,
    position: ContainerPosition,
    tooltip: HTMLElement,
  ): ArrowPosition => {
    const tooltipHeight = tooltip.getBoundingClientRect().height;
    const tooltipWidth = tooltip.getBoundingClientRect().width;
    const container = getContainerRect();

    if (actualArrowPosition === 'right' && Math.ceil(position.left - tooltipWidth) < 0) {
      return 'left';
    }
    if (actualArrowPosition === 'left' && Math.ceil(position.right + tooltipWidth) > container.width) {
      return 'right';
    }
    if (actualArrowPosition === 'bottom' && Math.ceil(position.top - tooltipHeight) < 0) {
      return 'top';
    }
    if (actualArrowPosition === 'top' && Math.ceil(position.bottom + tooltipHeight) > container.height) {
      return 'bottom';
    }

    // Flip between bottom-topright and topright if vertical overflow
    if (actualArrowPosition === 'bottom-topright' && Math.ceil(position.top - tooltipHeight) < 0) {
      return 'topright';
    }
    if (actualArrowPosition === 'topright' && Math.ceil(position.bottom + tooltipHeight) > container.height) {
      return 'bottom-topright';
    }

    return actualArrowPosition;
  };

  const onMouseEnter = () => {
    if (!isVisible || cancelTooltip) {
      return;
    }
    cancelHide(); // if a previous tip hasn't timed out yet, cancel it so it doesn't hide THIS tip
    if (!componentRef.current || !text) {
      return;
    }

    const tooltip = document.getElementById('tooltip');
    if (!tooltip) {
      return;
    }

    const viewportRect = componentRef.current.getBoundingClientRect();
    const position = toContainerCoords(viewportRect);
    tooltip.innerText = text;
    tooltip.setAttribute('data-visible', 'measure'); // ensure element displays so its height can be detected
    // Batch initial visibility change for measurement
    tooltip.setAttribute('style', 'display: block; opacity: 0;');

    const arrowPos = checkArrowPosition(actualArrowPosition, position, tooltip); // get correct position for arrow
    const { topOffset, leftOffset } = calculateTooltipPosition(position, arrowPos, isRtl());

    tooltip.setAttribute('data-arrow-position', arrowPos);
    // Constrain tooltip width based on available container space to prevent horizontal overflow
    const gutter = 12; // minimum space between tooltip and container edge
    const containerPadding = 8; // small padding from container edges
    const containerWidth = getContainerWidth();
    let maxWidthPx = '';
    if (arrowPos === 'right') {
      const computedLeft = Math.floor(position.left - gutter - containerPadding);
      maxWidthPx = `max(120px, ${computedLeft}px)`;
    } else if (arrowPos === 'left') {
      const computedRight = Math.floor(containerWidth - position.right - gutter - containerPadding);
      maxWidthPx = `max(120px, ${computedRight}px)`;
    } else {
      // for top/bottom variants, allow a reasonable max width without forcing overflow
      maxWidthPx = `max(120px, ${containerWidth - containerPadding * 2}px)`;
    }

    // Apply position and sizing in a single batched write
    const styles = [
      `top: ${topOffset}px`,
      `left: ${leftOffset}px`,
      `max-width: ${maxWidthPx}`,
      'display: block',
      'opacity: 1',
    ].join('; ');
    tooltip.setAttribute('data-visible', 'true');
    tooltip.setAttribute('style', styles);
  };

  const onMouseLeave = () => {
    if (cancelTooltip) {
      return;
    }
    hideTimerClearRef = setTimeout(hideTooltip, 200);
  };

  const tooltipLayerExists = typeof document !== 'undefined' && Boolean(document.getElementById('tooltip'));
  // Added a prop boolean here to be able to test ToolTip component. As it relies on document.getElementById
  // to find the tooltiplayer this is never true in the test as getElementById fires before
  // the component DOM is loaded. Or something like that.
  return tooltipLayerExists || import.meta.env?.MODE === 'test' ? (
    <div
      data-testid={dataTestId}
      className={className}
      ref={componentRef}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onFocus={onMouseEnter}
      onBlur={onMouseLeave}
      onClick={hideTooltip}
      onKeyDown={handleKeyboardSelect(hideTooltip)}
    >
      {children}
    </div>
  ) : (
    children
  );
};

export default Tooltip;
